import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge, SmartDataTable, Modal, Input, InputAmount } from '@/backoffice/components/ui';
import { formatCurrency, formatDate } from '@/backoffice/utils';
import {
  fetchDriverWallet,
  withdrawDriverWallet,
  type DriverWalletData,
  type DriverWalletTransaction,
} from '@/domains/driver/services/driver.service';
import {
  Wallet,
  TrendingUp,
  Clock,
  Check,
  FileText,
  ArrowUpRight,
  ArrowDownLeft,
  Loader2,
} from 'lucide-react';

type Period = 'today' | 'week' | 'month';

export function ProEarningsPage() {
  const [wallet, setWallet] = useState<DriverWalletData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<Period>('week');
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    amount: 0,
    payer_phone: '',
    mobile_money_provider: 'orange' as 'orange' | 'mtn',
  });

  const load = () => {
    setLoading(true);
    fetchDriverWallet()
      .then(setWallet)
      .catch(() => setWallet(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const earningsData = wallet
    ? {
        today: {
          label: "Aujourd'hui",
          earnings: wallet.today_earnings,
          deliveries: wallet.today_deliveries,
        },
        week: {
          label: 'Cette semaine',
          earnings: wallet.week_earnings,
          deliveries: wallet.week_deliveries,
        },
        month: {
          label: 'Ce mois',
          earnings: wallet.month_earnings,
          deliveries: wallet.month_deliveries,
        },
      }
    : null;

  const activeData = earningsData?.[period];

  const handleWithdraw = async () => {
    if (!withdrawForm.amount || !withdrawForm.payer_phone) return;
    setWithdrawing(true);
    try {
      const updated = await withdrawDriverWallet(withdrawForm);
      setWallet(updated);
      setShowWithdraw(false);
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userType="pro_driver" title="Revenus">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="pro_driver" title="Revenus">
      <div className="flex items-center gap-2 mb-6">
        {(['today', 'week', 'month'] as Period[]).map((p) => (
          <Button key={p} variant={period === p ? 'primary' : 'outline'} onClick={() => setPeriod(p)}>
            {earningsData?.[p].label}
          </Button>
        ))}
      </div>

      <Card className="mb-6 bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sm text-emerald-700">{activeData?.label}</p>
            <p className="text-4xl font-bold text-emerald-800">{formatCurrency(activeData?.earnings ?? 0)}</p>
          </div>
          <div className="w-16 h-16 rounded-full bg-emerald-200 flex items-center justify-center">
            <Wallet className="w-8 h-8 text-emerald-700" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="p-3 bg-white/50 rounded-lg">
            <p className="text-sm text-emerald-700">Livraisons</p>
            <p className="text-xl font-semibold text-emerald-800">{activeData?.deliveries ?? 0}</p>
          </div>
          <div className="p-3 bg-white/50 rounded-lg">
            <p className="text-sm text-emerald-700">Moyen/livraison</p>
            <p className="text-xl font-semibold text-emerald-800">
              {formatCurrency(
                activeData && activeData.deliveries > 0
                  ? Math.round(activeData.earnings / activeData.deliveries)
                  : 0,
              )}
            </p>
          </div>
          <div className="p-3 bg-white/50 rounded-lg">
            <p className="text-sm text-emerald-700">Note</p>
            <p className="text-xl font-semibold text-emerald-800">{wallet?.average_rating ?? 0}/5</p>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total cumulé</p>
              <p className="text-xl font-semibold text-gray-900">{formatCurrency(wallet?.total_earnings ?? 0)}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Solde disponible</p>
              <p className="text-xl font-semibold text-gray-900">{formatCurrency(wallet?.balance ?? 0)}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">En attente</p>
              <p className="text-xl font-semibold text-gray-900">{formatCurrency(wallet?.pending_amount ?? 0)}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Taux de complétion</p>
              <p className="text-xl font-semibold text-gray-900">{wallet?.completion_rate ?? 0}%</p>
            </div>
          </div>
        </Card>
      </div>

      <Card className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Retrait</h3>
          <Button leftIcon={<ArrowUpRight className="w-4 h-4" />} onClick={() => setShowWithdraw(true)}>
            Retirer les gains
          </Button>
        </div>
        <p className="text-sm text-gray-600">
          Solde disponible : <span className="font-semibold text-emerald-600">{formatCurrency(wallet?.balance ?? 0)}</span>
        </p>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Historique des transactions</h3>
          <Button variant="outline" size="sm" leftIcon={<FileText className="w-4 h-4" />}>
            Exporter
          </Button>
        </div>

        <SmartDataTable
          data={wallet?.transactions ?? []}
          columns={[
            {
              key: 'created_at',
              title: 'Date',
              sortable: true,
              render: (item: DriverWalletTransaction) => formatDate(item.created_at),
            },
            {
              key: 'type',
              title: 'Type',
              render: (item: DriverWalletTransaction) => (
                <div className="flex items-center gap-2">
                  {item.type === 'withdrawal' ? (
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <ArrowUpRight className="w-4 h-4 text-red-600" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <ArrowDownLeft className="w-4 h-4 text-emerald-600" />
                    </div>
                  )}
                  <span className="capitalize">
                    {item.type === 'withdrawal' ? 'Retrait' : item.type === 'order_payment' ? 'Livraison' : 'Autre'}
                  </span>
                </div>
              ),
            },
            {
              key: 'amount',
              title: 'Montant',
              sortable: true,
              render: (item: DriverWalletTransaction) => (
                <p className={`font-semibold ${item.type === 'withdrawal' ? 'text-red-600' : 'text-emerald-600'}`}>
                  {item.type === 'withdrawal' ? '-' : '+'}
                  {formatCurrency(item.amount)}
                </p>
              ),
            },
            {
              key: 'status',
              title: 'Statut',
              render: (item: DriverWalletTransaction) => {
                const config = {
                  completed: { variant: 'success' as const, label: 'Complété' },
                  pending: { variant: 'warning' as const, label: 'En cours' },
                  failed: { variant: 'danger' as const, label: 'Échec' },
                }[item.status] ?? { variant: 'default' as const, label: item.status };
                return <Badge variant={config.variant} dot>{config.label}</Badge>;
              },
            },
          ]}
          keyExtractor={(item) => item.id}
          emptyMessage="Aucune transaction"
        />
      </Card>

      <Modal isOpen={showWithdraw} onClose={() => setShowWithdraw(false)} title="Retirer mes gains">
        <div className="space-y-4">
          <InputAmount
            label="Montant (GNF)"
            value={withdrawForm.amount}
            onChange={(v) => setWithdrawForm((f) => ({ ...f, amount: v }))}
          />
          <Input
            label="Numéro Mobile Money"
            value={withdrawForm.payer_phone}
            onChange={(e) => setWithdrawForm((f) => ({ ...f, payer_phone: e.target.value }))}
            placeholder="+224620000000"
          />
          <div className="flex gap-2">
            {(['orange', 'mtn'] as const).map((provider) => (
              <Button
                key={provider}
                variant={withdrawForm.mobile_money_provider === provider ? 'primary' : 'outline'}
                onClick={() => setWithdrawForm((f) => ({ ...f, mobile_money_provider: provider }))}
              >
                {provider === 'orange' ? 'Orange Money' : 'MTN MoMo'}
              </Button>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowWithdraw(false)}>Annuler</Button>
            <Button isLoading={withdrawing} onClick={handleWithdraw}>Confirmer</Button>
          </div>
        </div>
      </Modal>
    </DashboardLayout>
  );
}
